import { useEffect, useState } from 'react';
import ErrorMessage from '../components/ErrorMessage';
import LoadingState from '../components/LoadingState';
import UploadSummary from '../components/UploadSummary';
import { useActiveUpload } from '../contexts/ActiveUploadContext';
import { useAuth } from '../contexts/AuthContext';
import { getColumnMapping } from '../services/settingsService';
import { uploadProjectMilestoneReport } from '../services/uploadService';

export default function UploadPage() {
  const { user } = useAuth();
  const { refreshActiveUpload } = useActiveUpload();
  const [file, setFile] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [loadingMapping, setLoadingMapping] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [parsed, setParsed] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function loadMapping() {
      try {
        const result = await getColumnMapping();
        if (!cancelled) setMapping(result.mapping);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoadingMapping(false);
      }
    }
    loadMapping();
    return () => { cancelled = true; };
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setSuccess('');
    setParsed(null);
    setProcessing(true);
    try {
      const result = await uploadProjectMilestoneReport(file, user, mapping);
      setParsed(result.parsed);
      setSuccess(`${result.upload.file_name} was imported and activated successfully.`);
      await refreshActiveUpload();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  }

  if (loadingMapping) return <LoadingState message="Loading column mapping..." />;

  return (
    <div className="page-stack">
      <div className="page-title-row">
        <div>
          <h2>Upload Report</h2>
          <p>Upload a Salesforce-generated Project Milestone Report .xlsx file.</p>
        </div>
      </div>
      <ErrorMessage message={error} />
      {success && <div className="success-message">{success}</div>}
      <form className="panel upload-form" onSubmit={handleSubmit}>
        <label className="file-drop">
          <input
            type="file"
            accept=".xlsx"
            onChange={(event) => setFile(event.target.files?.[0] || null)}
          />
          <strong>{file?.name || 'Choose .xlsx Project Milestone Report'}</strong>
          <span>Raw rows, cleaned rows, and summary tables are stored after processing.</span>
        </label>
        <button type="submit" className="button primary" disabled={!file || processing}>
          {processing ? 'Processing upload...' : 'Upload and Process'}
        </button>
      </form>
      {processing && <LoadingState message="Parsing workbook, cleaning rows, and writing Supabase records..." />}
      <UploadSummary parsed={parsed} />
    </div>
  );
}
